import { create } from 'zustand';
import type { SessionData } from './session';
import { isSessionExpired, parseSession } from './session';
import { apiAxios } from '~/lib/api/client';
import { sessionStorageAdapter } from './storage';

interface AuthState {
	session: SessionData | null;
	signIn: (userName: string, password: string) => Promise<void>;
	signOut: () => void;
}

/**
 * Reactive auth state over the localStorage session. Hydrates on creation and
 * stays in sync across tabs (and with the legacy admin when same-origin) via the
 * adapter's storage subscription.
 */
export const useAuthStore = create<AuthState>(set => {
	sessionStorageAdapter.subscribe(() => set({ session: sessionStorageAdapter.load() }));
	return {
		session: sessionStorageAdapter.load(),
		signIn: async (userName, password) => {
			// Server LoginArgs binds the terse codes `usr`/`psw` (matches the legacy admin).
			const { data } = await apiAxios.post('/v1/account/login', { usr: userName, psw: password });
			const session = parseSession(data);
			if (!session?.accessToken) throw new Error('login_failed');
			sessionStorageAdapter.save(session);
			set({ session });
		},
		signOut: () => {
			sessionStorageAdapter.clear();
			set({ session: null });
		}
	};
});

/** Reactive auth check for guards/components. */
export function useIsAuthenticated(): boolean {
	return useAuthStore(s => !!s.session && !isSessionExpired(s.session));
}
